import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

/**
 * IMPORTANT — Clipping requirement when using outside assignment bars:
 *
 * During scroll, this component removes `overflow: hidden` (via removing `truncate`)
 * and animates text with `translateX`. The scrolling text will visually escape its
 * own bounds. On assignment bars this is fine because the bar has `overflow-hidden`.
 *
 * When used in other contexts (tooltips, modals, cards), you MUST wrap ScrollableText
 * in a parent element with `overflow-hidden` to act as the clipping boundary:
 *
 * ```tsx
 * // ✅ CORRECT — parent clips the scrolling text
 * <div className="overflow-hidden">
 *   <ScrollableText>Long text here</ScrollableText>
 * </div>
 *
 * // ❌ WRONG — text will overlap siblings during scroll animation
 * <ScrollableText>Long text here</ScrollableText>
 * ```
 *
 * The clipping parent also needs proper width constraints (e.g. `min-w-0` if it's
 * a flex item) so it doesn't grow to fit the unwrapped text content.
 */
interface ScrollableTextProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  animationDelayMs?: number;
  /** When provided, the parent controls hover state instead of internal mouse events */
  externalHovered?: boolean;
  /** Freeze the animation at its current position without resetting */
  paused?: boolean;
}

const FADE_WIDTH = 16; // px — width of the fade gradient on each side

const FADE_BOTH = `linear-gradient(to right, transparent, black ${FADE_WIDTH}px, black calc(100% - ${FADE_WIDTH}px), transparent)`;

/**
 * Check if an element's content overflows its visible area.
 * Looks for a nested .truncate element first, then falls back to the element itself.
 */
function measureOverflow(element: HTMLElement): { isTruncated: boolean; distance: number } {
  const truncateChild = element.querySelector('.truncate') as HTMLElement | null;
  const target =
    truncateChild && truncateChild.scrollWidth > truncateChild.clientWidth
      ? truncateChild
      : element;
  const isTruncated = target.scrollWidth > target.clientWidth;
  const distance = isTruncated ? target.scrollWidth - target.clientWidth + 4 : 0;
  return { isTruncated, distance };
}

export const ScrollableText = ({
  children,
  className,
  style,
  disabled = false,
  animationDelayMs = 100,
  externalHovered,
  paused = false,
}: ScrollableTextProps) => {
  const isExternallyControlled = externalHovered !== undefined;
  const [shouldScroll, setShouldScroll] = useState(false);
  const innerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollDistanceRef = useRef(0);

  // Start scroll animation if content is truncated
  const startScrollIfTruncated = useCallback(() => {
    if (!innerRef.current || disabled) return;

    const { isTruncated, distance } = measureOverflow(innerRef.current);
    if (isTruncated) {
      scrollDistanceRef.current = distance;
      scrollTimeoutRef.current = setTimeout(() => {
        setShouldScroll(true);
      }, animationDelayMs);
    }
  }, [disabled, animationDelayMs]);

  // Stop scroll animation and clear pending timeout
  const stopScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    setShouldScroll(false);
  }, []);

  // Handle mouse enter - check truncation and start scroll after delay
  const handleMouseEnter = useCallback(() => {
    startScrollIfTruncated();
  }, [startScrollIfTruncated]);

  // React to external hover state when parent controls hover
  useEffect(() => {
    if (!isExternallyControlled) return;

    if (externalHovered) {
      startScrollIfTruncated();
    } else {
      stopScroll();
    }
  }, [externalHovered, isExternallyControlled, startScrollIfTruncated, stopScroll]);

  // Stop animation when disabled
  useEffect(() => {
    if (disabled) stopScroll();
  }, [disabled, stopScroll]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // When actively scrolling, apply both-sides fade mask
  const scrollingFadeMask = shouldScroll && scrollDistanceRef.current > 0 ? FADE_BOTH : undefined;

  return (
    <div
      className={cn(className, 'overflow-hidden')}
      style={{
        ...style,
        ...(scrollingFadeMask && {
          WebkitMaskImage: scrollingFadeMask,
          maskImage: scrollingFadeMask,
        }),
      }}
      onMouseEnter={isExternallyControlled ? undefined : handleMouseEnter}
      onMouseLeave={isExternallyControlled ? undefined : stopScroll}
    >
      <div
        ref={innerRef}
        className={cn({
          truncate: !shouldScroll,
          'scrollable-text-scrolling': shouldScroll,
        })}
        style={
          shouldScroll && scrollDistanceRef.current > 0
            ? {
                animation: 'billboard-scroll 3s linear infinite',
                animationPlayState: paused ? 'paused' : 'running',
                whiteSpace: 'nowrap',
                ['--scroll-distance' as string]: `-${scrollDistanceRef.current}px`,
              }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  );
};
